import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Chart from "react-apexcharts";
import dashboardReportsApi from "../../api/dashboardReportsApi";
import Loading from "../../components/Loading";
import { ComplianceTrackerGrid, BeforeMarchDashboard } from "../../components/compliance";

export default function DashboardReport() {
  const token = useSelector((state) => state.auth.access_token);
  const [data, setData] = useState({
    userMonthlyActivity: [],
    allTasksStatus: [],
    allTaskClosures: [],
    lastRefreshTime: null,
    metricsAccount: [],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Calculate aggregated metrics from metricsAccount
  const aggregatedMetrics = data.metricsAccount.reduce(
  (acc, account) => {
    acc.totalAccounts += 1;
    acc.accountsThisMonth += account.accountsThisMonth || 0;
    acc.totalTasks += account.totalTasks || 0;
    acc.tasksThisMonth += account.tasksThisMonth || 0;
    acc.totalWorkflows += account.totalWorkflows || 0;
    acc.workflowsThisMonth += account.workflowsThisMonth || 0;

    return acc;
  },
  {
    totalAccounts: 0,
    accountsThisMonth: 0,
    totalTasks: 0,
    tasksThisMonth: 0,
    totalWorkflows: 0,
    workflowsThisMonth: 0,
  }
);


  // Calculate aggregated task status
  const aggregatedTaskStatus = data.allTasksStatus.reduce(
    (acc, account) => ({
      totalTodo: (acc.totalTodo || 0) + (account.totalTodo || 0),
      totalProposed: (acc.totalProposed || 0) + (account.totalProposed || 0),
      totalInProgress:
        (acc.totalInProgress || 0) + (account.totalInProgress || 0),
      totalPending: (acc.totalPending || 0) + (account.totalPending || 0),
      totalOverdue: (acc.totalOverdue || 0) + (account.totalOverdue || 0),
      totalInReview: (acc.totalInReview || 0) + (account.totalInReview || 0),
      totalCompleted: (acc.totalCompleted || 0) + (account.totalCompleted || 0),
      totalClosed: (acc.totalClosed || 0) + (account.totalClosed || 0),
      todoThisMonth: (acc.todoThisMonth || 0) + (account.todoThisMonth || 0),
      proposedThisMonth:
        (acc.proposedThisMonth || 0) + (account.proposedThisMonth || 0),
      inProgressThisMonth:
        (acc.inProgressThisMonth || 0) + (account.inProgressThisMonth || 0),
      pendingThisMonth:
        (acc.pendingThisMonth || 0) + (account.pendingThisMonth || 0),
      overdueThisMonth:
        (acc.overdueThisMonth || 0) + (account.overdueThisMonth || 0),
      inReviewThisMonth:
        (acc.inReviewThisMonth || 0) + (account.inReviewThisMonth || 0),
      completedThisMonth:
        (acc.completedThisMonth || 0) + (account.completedThisMonth || 0),
      closedThisMonth:
        (acc.closedThisMonth || 0) + (account.closedThisMonth || 0),
    }),
    {},
  );

  const fetchAllData = async () => {
    try {
      setLoading(true);

      const [
        userMonthlyActivity,
        allTasksStatus,
        allTaskClosures,
        lastRefreshTime,
        metricsAccount,
      ] = await Promise.all([
        dashboardReportsApi.getUserMonthlyActivity(token),
        dashboardReportsApi.getAllTasksStatus(token),
        dashboardReportsApi.getAllTaskClosures(token),
        dashboardReportsApi.getLastRefreshTime(token),
        dashboardReportsApi.getMetricsAccount(token),
      ]);

      setData({
        userMonthlyActivity: userMonthlyActivity || [],
        allTasksStatus: allTasksStatus || [],
        allTaskClosures: allTaskClosures || [],
        lastRefreshTime: lastRefreshTime,
        metricsAccount: metricsAccount || [],
      });
    } catch {
      setError("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const manualRefresh = async () => {
    await dashboardReportsApi.manualRefresh(token);
    fetchAllData();
  };

  useEffect(() => {
    if (token) {
      fetchAllData();
    }
  }, [token]);

  if (loading) return <Loading />;

  const formatDateTime = (date) => {
    if (!date) return "--";
    return new Intl.DateTimeFormat("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(date));
  };

  // Task status for chart
  const statusSeries = [
    aggregatedTaskStatus.totalTodo || 0,
    aggregatedTaskStatus.totalInProgress || 0,
    aggregatedTaskStatus.totalInReview || 0,
    aggregatedTaskStatus.totalCompleted || 0,
    aggregatedTaskStatus.totalOverdue || 0,
    aggregatedTaskStatus.totalPending || 0,
  ];

  const hasStatusData = statusSeries.some((v) => v > 0);

  const statusChartOptions = {
    labels: [
      "Todo",
      "In Progress",
      "Review",
      "Completed",
      "Overdue",
      "Pending",
    ],
    legend: { position: "bottom", fontSize: "11px" },
    plotOptions: {
      pie: {
        donut: {
          size: "60%",
        },
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return Math.round(val) + "%";
      },
      style: {
        fontSize: "10px",
      },
    },
  };

  // User activity chart data
  const topPerformers = [...data.userMonthlyActivity]
    .sort(
      (a, b) =>
        b.totalTaskInteractionsThisMonth - a.totalTaskInteractionsThisMonth,
    )
    .slice(0, 5);

  const performanceSeries = topPerformers.map(
    (user) => user.totalTaskInteractionsThisMonth,
  );
  const performanceLabels = topPerformers.map(
    (user) => user.username.split(" ")[0],
  );

  const performanceChartOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        dataLabels: {
          position: "top",
        },
      },
    },
    dataLabels: {
      enabled: true,
      offsetX: 0,
      style: {
        fontSize: "10px",
        colors: ["#fff"],
      },
      formatter: function (val) {
        return val;
      },
    },
    xaxis: {
      categories: performanceLabels,
      labels: { style: { fontSize: "10px" } },
      title: {
        text: "Total Interactions",
        style: { fontSize: "10px" },
      },
    },
    yaxis: {
      labels: { style: { fontSize: "10px" } },
    },
    colors: ["#4F46E5"],
    tooltip: {
      y: {
        formatter: function (val) {
          return val + " interactions this month";
        },
      },
    },
  };

  return (
    <div>
      <div className="bg-white px-4 py-1 rounded-lg shadow-sm border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
        <div className="flex-1">
          {/* <h1 className="font-bold text-xl text-slate-800">
            Enterprise Dashboard
          </h1> */}
          <div className="flex flex-wrap gap-3 text-sm mt-1">
            <p className="text-slate-600">
              <strong>Last Refresh:</strong>{" "}
              {formatDateTime(data.lastRefreshTime?.lastRefreshedAt)}
            </p>
            <p className="text-slate-600">
              <strong>Next Refresh:</strong>{" "}
              {data.lastRefreshTime?.nextScheduledRefresh}
            </p>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <button
            onClick={manualRefresh}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
          >
            Refresh Data
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 mb-2">
        <div className="bg-white px-2 py-1 rounded-lg shadow-sm border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800 mb-3 pb-2 border-b">
            Aggregated Metrics
          </h3>
          <div className="space-y-2.5">
            <MetricRow
              label="Total Accounts"
              value={aggregatedMetrics.totalAccounts || 0}
            />
            <MetricRow
              label="Accounts This Month"
              value={aggregatedMetrics.accountsThisMonth || 0}
              highlight
            />
            <MetricRow
              label="Total Tasks"
              value={aggregatedMetrics.totalTasks || 0}
            />
            <MetricRow
              label="Tasks This Month"
              value={aggregatedMetrics.tasksThisMonth || 0}
              highlight
            />
            <MetricRow
              label="Total Workflows"
              value={aggregatedMetrics.totalWorkflows || 0}
            />
            <MetricRow
              label="Workflows This Month"
              value={aggregatedMetrics.workflowsThisMonth || 0}
              highlight
            />
          </div>
        </div>

        <div className="bg-white px-2 py-1 rounded-lg shadow-sm border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800 mb-3 pb-2 border-b">
            Task Status Overview
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-slate-600 mb-1">Total</h4>
              <MetricRow
                label="Todo"
                value={aggregatedTaskStatus.totalTodo}
                small
              />
              <MetricRow
                label="In Progress"
                value={aggregatedTaskStatus.totalInProgress}
                small
              />
              <MetricRow
                label="In Review"
                value={aggregatedTaskStatus.totalInReview}
                small
              />
              <MetricRow
                label="Completed"
                value={aggregatedTaskStatus.totalCompleted}
                success
                small
              />
              <MetricRow
                label="Closed"
                value={aggregatedTaskStatus.totalClosed}
                success
                small
              />
              <MetricRow
                label="Overdue"
                value={aggregatedTaskStatus.totalOverdue}
                danger
                small
              />
              <MetricRow
                label="Pending"
                value={aggregatedTaskStatus.totalPending}
                small
              />
            </div>
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-slate-600 mb-1">
                This Month
              </h4>
              <MetricRow
                label="Todo"
                value={aggregatedTaskStatus.todoThisMonth}
                small
              />
              <MetricRow
                label="In Progress"
                value={aggregatedTaskStatus.inProgressThisMonth}
                small
              />
              <MetricRow
                label="In Review"
                value={aggregatedTaskStatus.inReviewThisMonth}
                small
              />
              <MetricRow
                label="Completed"
                value={aggregatedTaskStatus.completedThisMonth}
                success
                small
              />
              <MetricRow
                label="Closed"
                value={aggregatedTaskStatus.closedThisMonth}
                success
                small
              />
              <MetricRow
                label="Overdue"
                value={aggregatedTaskStatus.overdueThisMonth}
                danger
                small
              />
              <MetricRow
                label="Pending"
                value={aggregatedTaskStatus.pendingThisMonth}
                small
              />
            </div>
          </div>
        </div>

        <div className="bg-white px-2 py-1 rounded-lg shadow-sm border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">
            Task Distribution
          </h3>
          {hasStatusData ? (
            <Chart
              options={statusChartOptions}
              series={statusSeries}
              type="donut"
              height={220}
            />
          ) : (
            <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">
              No task data available
            </div>
          )}
        </div>
      </div>

      <div className="bg-white px-4 py-1 rounded-lg shadow-sm border border-slate-200 lg:col-span-2 xl:col-span-3 mb-2 max-h-[350px] overflow-auto">
        <h3 className="text-sm font-semibold text-slate-800 mb-3 pb-2 border-b">
          User Activity (This Month)
        </h3>

        <div className="grid grid-cols-3 gap-3 items-stretch">
          {data.userMonthlyActivity.map((user, idx) => (
            <div
              key={idx}
              className="px-3 py-1 bg-white rounded border border-slate-200 h-full flex flex-col"
            >
              <div className="flex justify-between items-start mb-2">
                <div>
                  <div className="font-medium text-sm text-slate-800">
                    {user.username}
                  </div>
                  <div className="text-xs text-slate-500">{user.userEmail}</div>
                  {/* <div className="text-xs text-slate-400 mt-1">
                    {user.userRole}
                  </div> */}
                </div>

                <div className="text-right">
                  <div className="font-bold text-indigo-600 text-[12px]">
                    {user.userRole}
                  </div>

                </div>
                <div className="text-right">
                  <div className="font-bold text-indigo-600 text-sm">
                    {user.totalTaskInteractionsThisMonth}
                  </div>
                  <div className="text-xs text-slate-500">
                    total interactions
                  </div>
                </div>
              </div>

              <div className="flex-grow" />

              <div className="grid grid-cols-3 gap-1 text-xs">
                <div className="text-slate-600">
                  Created:{" "}
                  <span className="font-medium">
                    {user.tasksCreatedThisMonth}
                  </span>
                </div>
                <div className="text-slate-600">
                  Updated:{" "}
                  <span className="font-medium">
                    {user.tasksUpdatedThisMonth}
                  </span>
                </div>
                <div className="text-slate-600">
                  Assigned:{" "}
                  <span className="font-medium">
                    {user.tasksAssignedThisMonth}
                  </span>
                </div>
                <div className="text-slate-600">
                  Completed:{" "}
                  <span className="font-medium">
                    {user.tasksCompletedThisMonth}
                  </span>
                </div>
                <div className="text-slate-600">
                  Closed:{" "}
                  <span className="font-medium">
                    {user.tasksClosedThisMonth}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2  gap-4 mb-2">
       

        <div className="bg-white px-4 py-1 rounded-lg shadow-sm border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">
            Top Performers (This Month)
          </h3>
          {performanceSeries.length > 0 ? (
            <Chart
              options={performanceChartOptions}
              series={[{ data: performanceSeries }]}
              type="bar"
              height={220}
            />
          ) : (
            <div className="h-[220px] flex items-center justify-center text-sm text-slate-400">
              No user activity data
            </div>
          )}
        </div>

        {/* <div className="bg-white px-4 py-1 rounded-lg shadow-sm border border-slate-200">
          <h3 className="text-sm font-semibold text-slate-800 mb-3 pb-2 border-b">
            Task Closures
          </h3>
          <div className="space-y-3">
            {data.allTaskClosures.slice(0, 5).map((closure, idx) => (
              <div
                key={idx}
                className="p-3 bg-slate-50 rounded border border-slate-200"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="font-medium text-sm text-slate-800">
                      {closure.username}
                    </div>
                    <div className="text-xs text-slate-500">
                      {closure.userEmail}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {closure.accountName}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-indigo-600 text-sm">
                      {closure.tasksClosedThisMonth}
                    </div>
                    <div className="text-xs text-slate-500">this month</div>
                    <div className="text-xs font-medium text-slate-700">
                      {closure.totalTasksClosed} total
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {data.allTaskClosures.length === 0 && (
              <div className="text-center py-8 text-sm text-slate-400">
                No task closure data
              </div>
            )}
          </div>
        </div> */}
<div className="bg-white px-4 py-1 rounded-lg shadow-sm border border-slate-200  max-h-[350px] overflow-auto">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">
            Accounts Task Status
          </h3>
          <div className="space-y-3">
            {data.allTasksStatus.map((account, idx) => (
              <div
                key={idx}
                className="px-3 py-1 border border-slate-200 rounded hover:bg-slate-50"
              >
                <div className="flex justify-between items-center mb-2">
                  <div className="font-medium text-sm text-slate-800">
                    {account.accountName}
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-emerald-600 text-sm">
                      {(account.totalCompleted || 0) +
                        (account.totalClosed || 0)}
                    </div>
                    <div className="text-xs text-slate-500">
                      completed/closed
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 text-xs">
                  <div className="text-slate-600">
                    <span className="font-medium">
                      {account.totalTodo || 0}
                    </span>{" "}
                    todo
                  </div>
                  <div className="text-slate-600">
                    <span className="font-medium">
                      {account.totalInProgress || 0}
                    </span>{" "}
                    in progress
                  </div>
                  <div className="text-slate-600">
                    <span className="font-medium">
                      {account.totalInReview || 0}
                    </span>{" "}
                    in review
                  </div>
                  <div className="text-slate-600">
                    <span className="font-medium">
                      {account.totalPending || 0}
                    </span>{" "}
                    pending
                  </div>
                  <div className="text-red-600">
                    <span className="font-medium">
                      {account.totalOverdue || 0}
                    </span>{" "}
                    overdue
                  </div>
                  <div className="text-emerald-600">
                    <span className="font-medium">
                      {account.totalClosed || 0}
                    </span>{" "}
                    closed
                  </div>
                </div>
              </div>
            ))}
            {data.allTasksStatus.length === 0 && (
              <div className="text-center py-8 text-sm text-slate-400">
                No task status data
              </div>
            )}
          </div>
        </div>
        
      </div>
       <div className="bg-white px-4 py-1 rounded-lg shadow-sm border border-slate-200 lg:col-span-2 xl:col-span-3 mb-2 max-h-[350px] overflow-auto">
          <h3 className="text-sm font-semibold text-slate-800 mb-3">
            Accounts Performance
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-2 font-medium text-slate-600">
                    Account
                  </th>
                  <th className="text-left py-2 font-medium text-slate-600">
                    Industry
                  </th>
                  <th className="text-right py-2 font-medium text-slate-600">
                    Accounts
                  </th>
                  <th className="text-right py-2 font-medium text-slate-600">
                    Accounts (M)
                  </th>
                  <th className="text-right py-2 font-medium text-slate-600">
                    Tasks
                  </th>
                  <th className="text-right py-2 font-medium text-slate-600">
                    Tasks (M)
                  </th>
                  <th className="text-right py-2 font-medium text-slate-600">
                    Workflows
                  </th>
                  <th className="text-right py-2 font-medium text-slate-600">
                    Workflows (M)
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.metricsAccount.length > 0 ? (
                  data.metricsAccount.map((account, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-slate-100 hover:bg-slate-50"
                    >
                      <td className="py-2">
                        <div className="font-medium text-slate-800">
                          {account.accountName}
                        </div>
                      </td>
                      <td className="py-2 text-slate-600">
                        {account.industryType}
                      </td>
                      <td className="py-2 text-right font-medium">
                        {account.totalAccounts}
                      </td>
                      <td className="py-2 text-right">
                        <span className="font-medium text-indigo-600">
                          {account.accountsThisMonth}
                        </span>
                      </td>
                      <td className="py-2 text-right font-medium">
                        {account.totalTasks}
                      </td>
                      <td className="py-2 text-right">
                        <span className="font-medium text-indigo-600">
                          {account.tasksThisMonth}
                        </span>
                      </td>
                      <td className="py-2 text-right font-medium">
                        {account.totalWorkflows}
                      </td>
                      <td className="py-2 text-right">
                        <span className="font-medium text-indigo-600">
                          {account.workflowsThisMonth}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-6 text-center text-sm text-slate-400"
                    >
                      No account data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      {/* Compliance Tracker Section */}
      <div className="mt-4">
        <ComplianceTrackerGrid />
      </div>

      {/* Before March Dashboard Section */}
      <div className="mt-4">
        <BeforeMarchDashboard />
      </div>
    </div>
  );
}

/* ========================================================= */

const MetricRow = ({ label, value, highlight, danger, success, small }) => (
  <div className="flex justify-between items-center">
    <span className={`text-slate-600 ${small ? "text-xs" : "text-sm"}`}>
      {label}
    </span>
    <span
      className={`
        ${small ? "text-xs" : "text-sm"} font-medium
        ${highlight ? "text-indigo-600" : ""}
        ${danger ? "text-red-600" : ""}
        ${success ? "text-emerald-600" : ""}
        ${!highlight && !danger && !success ? "text-slate-800" : ""}
      `}
    >
      {value ?? 0}
    </span>
  </div>
);
